/* The notation panel: the spot in words and its copy buttons while reading, the editor while
   editing. */

import {
  LABEL,
  ORDER,
  actionLabel,
  applyNotes,
  notation,
  notesOf,
  presenceOf,
  setHeroRaise,
  spotStory,
  versus,
  type Spot,
  seatName,
} from "@preflop/core";
import { el, fill } from "../../ui/dom";
import { ACTION_COLOR } from "../../ui/grid";
import { brushBar, noteFields } from "./brush";
import { raiseSizeBox, raiseSizeOf, raiseSizeText, sameRaiseSize } from "./raise-size";
import { previewNow } from "./chart";
import { flash } from "./flash";
import { dom } from "./layout";
import { dataMessage } from "@preflop/api";
import { outsideFault, sameNotes, setEditGuard, showDraftState, trimNotes } from "./preview";
import { activeIsOwn, deleteChart, saveChart } from "./save";
import { page, profiles, render } from "./state";

/* The panel lives in the left column, under the tree that chose the spot, and under all of
   it: the opponent seats are part of the question, so the answer sits below them and not
   between them and the action they answer to. */
function placeNotation(): void {
  // The creator takes that column over, so the panel waits under the chart.
  if (page().creator || dom.picker.hidden) return parkNotation();
  if (dom.notation.parentNode !== dom.picker || dom.notation.nextSibling)
    dom.picker.append(dom.notation);
}

/* The left column is rebuilt from scratch on every render, which would take the panel with
   it: it sits out the rebuild under the chart. */
export function parkNotation(): void {
  if (dom.notation.nextSibling !== dom.store) dom.chart.insertBefore(dom.notation, dom.store);
}

/** The story as rows: the seat, and what it did. */
function storyList(spot: Spot): HTMLElement {
  return el(
    "ol",
    { class: "story" },
    spotStory(spot, profiles()).map((b) =>
      el(
        "li",
        { class: b.kind },
        el("span", { class: "who" }, seatName(b.who)),
        el("span", { class: "what" }, b.what),
      ),
    ),
  );
}

/* A range leaves this page to be pasted somewhere that takes one action at a time, so each
   action gets its own button. "All" hands over every list at once. */
function copySet(spot: Spot): HTMLElement | null {
  const acts = ORDER.filter((a) => a !== "fold" && spot.stats[a]);
  if (!acts.length) return null;

  const button = (label: string, colour: string | null, text: () => string): HTMLElement => {
    const btn = el("button", {
      type: "button",
      class: "copy",
      title: `Copy the ${label.toLowerCase()} list to the clipboard`,
    });
    // The label is redrawn rather than swapped out, so the colour dot survives the "Copied"
    // flash.
    const paint = (t: string): void =>
      fill(btn, colour ? el("i", { class: "dot", style: { background: colour } }) : null, t);
    paint(label);
    btn.onclick = async () => {
      try {
        await navigator.clipboard.writeText(text());
        paint("Copied");
      } catch {
        paint("Blocked");
      }
      setTimeout(() => paint(label), 1200);
    };
    return btn;
  };

  return el(
    "div",
    { class: "copyset" },
    el("span", { class: "lbl" }, "Copy"),
    acts.map((a) => button(LABEL[a], ACTION_COLOR[a], () => notation(spot, a))),
    acts.length > 1
      ? button("All", null, () => acts.map((a) => `${a}: ${notation(spot, a)}`).join("\n"))
      : null,
  );
}

export function renderNotation(spot: Spot): void {
  const p = page();
  const host = dom.notationBody;
  fill(host);
  setEditGuard(null, null); // rebuilt below when the editor is up
  dom.spotTitle.textContent = [
    seatName(spot.hero),
    actionLabel(spot.action),
    ...spot.seats.map(seatName),
    spot.openSize,
    // The profile is part of the question the chart answers, so the title says it too.
    ...(spot.profile ? [versus(profiles(), spot.profile)] : []),
  ].join(" · ");

  placeNotation();

  // One chart is edited at a time, and while a new spot is being filled in the chart column
  // belongs to the draft: the matrix draws it and a stroke paints into it. The panel would be
  // telling the story of the spot underneath, under a grid that is no longer its own, so it
  // steps aside until the creator closes.
  dom.notation.hidden = !!p.creator;
  if (p.creator) {
    p.dirty = false;
    p.editDraft = null;
    return;
  }

  if (!p.editMode) {
    p.dirty = false;
    p.editDraft = null;
    fill(host, storyList(spot), copySet(spot));
    return;
  }

  /* ----- edit mode ----- */
  const d =
    p.editDraft?.key === spot.key
      ? p.editDraft
      : (p.editDraft = {
          key: spot.key,
          notes: notesOf(spot),
          raise: raiseSizeText(spot),
          preview: null,
          error: "",
          warn: "",
          outside: [],
          priority: null,
        });

  const ed = el("div", { class: "editor" }, brushBar());
  noteFields(
    ed,
    d.notes,
    (a) => {
      p.dirty = true;
      d.priority = a;
      previewNow();
    },
    {
      under: {
        raise: raiseSizeBox(spot, d.raise, (text) => {
          d.raise = text;
          p.dirty = true;
        }),
      },
      // What a pasted solver range is read against: its weights are shares of the whole
      // deal, and this chart's are shares of the hands that got here.
      arriving: () => presenceOf(spot),
    },
  );

  ed.append(
    el(
      "p",
      { class: "hint" },
      "Pick an action and a weight, then click or drag on the matrix; shift-click takes that " +
        "action off a hand. Typing and painting are the same edit. A range copied out of GTO " +
        "Wizard is converted when you paste it into a field, and read against the hands hero " +
        "arrives with. Fold is the remainder and is computed for you. Another open size is " +
        "another chart: create it from the tree.",
    ),
  );

  const msg = el("p", { class: "msg err", hidden: true });
  ed.append(msg);
  const fail = (text: string): void => {
    msg.className = "msg err";
    msg.textContent = text;
    msg.hidden = false;
  };

  const save = el(
    "button",
    {
      type: "button",
      class: "copy primary",
      title: "Keep these hands as this chart",
      dataset: { testid: "save-chart" },
    },
    "Save",
  );
  // Both are the repaint's to set: the line says what the draft is, the button whether it
  // can be kept.
  setEditGuard(msg, save);
  showDraftState();
  const revert = el(
    "button",
    {
      type: "button",
      class: "copy",
      title: "Throw the edits away and show the stored chart",
      onclick: () => {
        p.editDraft = null;
        p.dirty = false;
        render();
      },
    },
    "Undo changes",
  );

  save.onclick = async () => {
    // Emptied fields are a chart of their own: hero folds every hand it holds here. The
    // matrix has been drawing that, and the line under the fields has been saying it, so the
    // edit is deliberate rather than a range wiped by accident.
    const notes = trimNotes(d.notes);
    if (sameNotes(notes, notesOf(spot)) && sameRaiseSize(spot, d.raise, raiseSizeText(spot)))
      return flash("Nothing to save: no change.", "info");
    // The greyed button is what the user sees; this is what actually holds. A rule that
    // lives only in a DOM attribute is not a rule.
    if (d.outside.length) return fail(outsideFault(d.outside));

    // Validated on a copy before any of it is kept: a typo leaves nothing half-saved.
    // Editing never moves a chart, so the copy keeps its place and its key.
    const cand = structuredClone(spot);
    let warn: string[];
    try {
      warn = applyNotes(cand, notes, d.priority);
      setHeroRaise(cand, raiseSizeOf(d.raise));
      // Into the active collection, or the one the edit bar names when this one is read-only.
      // Anything but "saved" leaves the draft as it is: the user kept the target's chart, or
      // the page is already reloading onto the collection the chart went into.
      if ((await saveChart(cand)) !== "saved") return;
    } catch (e) {
      return fail(dataMessage(e));
    }
    p.isolate = null;
    p.editDraft = null;
    p.dirty = false;
    render();
    // Said every time: the chart went over the network, and the user should know it arrived.
    flash([`Saved into "${p.session.collection.name}".`, ...warn].join(" · "), "info");
  };

  const bar = el("div", { class: "actions" }, save, revert);

  // A chart in a collection of the user's own can be removed from it. There is no second
  // chart underneath to go back to: the collection's chart is the chart.
  if (activeIsOwn()) {
    const name = p.session.collection.name;
    bar.append(
      el("span", { class: "spacer" }),
      el(
        "button",
        {
          type: "button",
          class: "copy danger",
          title: `Remove this chart from "${name}"`,
          dataset: { testid: "delete-chart" },
          onclick: async () => {
            if (!confirm(`Delete this chart from "${name}"? It cannot be brought back.`)) return;
            try {
              await deleteChart(spot.key);
            } catch (e) {
              return fail(dataMessage(e));
            }
            p.isolate = null;
            p.editDraft = null;
            p.dirty = false;
            render();
            flash("Chart deleted.", "info");
          },
        },
        "Delete chart",
      ),
    );
  }

  ed.append(bar);
  host.append(ed);
}
